import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	Check,
	Eye,
	EyeOff,
	KeyRound,
	Lock,
	ShieldCheck,
	X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { API_BASE_URL } from "@/lib/consts";
import { trackUserSignedUp } from "@/lib/analytics-events";

// --- Route definition ---

export const Route = createFileRoute("/auth/register-invite")({
	component: RegisterInvitePage,
	validateSearch: (search) =>
		z.object({ token: z.string().min(1) }).parse(search),
});

// --- Password requirements ---

interface PasswordRequirement {
	label: string;
	test: (pw: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
	{ label: "At least 8 characters", test: (pw) => pw.length >= 8 },
	{ label: "One uppercase letter", test: (pw) => /[A-Z]/.test(pw) },
	{ label: "One lowercase letter", test: (pw) => /[a-z]/.test(pw) },
	{ label: "One number", test: (pw) => /\d/.test(pw) },
	{
		label: "One special character",
		test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw),
	},
];

function getStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	if (!password) return { score: 0, label: "", color: "" };
	const passed = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;
	if (passed <= 2) return { score: 20, label: "Weak", color: "bg-red-500" };
	if (passed <= 3) return { score: 50, label: "Fair", color: "bg-amber-500" };
	if (passed <= 4) return { score: 75, label: "Good", color: "bg-blue-500" };
	return { score: 100, label: "Strong", color: "bg-green-500" };
}

// --- Main component ---

function RegisterInvitePage() {
	const { token } = Route.useSearch();
	const navigate = useNavigate();

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const requirementResults = useMemo(
		() => PASSWORD_REQUIREMENTS.map((r) => ({ ...r, met: r.test(password) })),
		[password],
	);

	const allRequirementsMet = requirementResults.every((r) => r.met);
	const passwordsMatch =
		password === confirmPassword && confirmPassword.length > 0;
	const canSubmit =
		firstName.trim().length > 0 &&
		lastName.trim().length > 0 &&
		allRequirementsMet &&
		passwordsMatch &&
		!submitting;

	const strength = useMemo(() => getStrength(password), [password]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!canSubmit) return;

			setSubmitting(true);
			setError(null);

			try {
				const res = await fetch(`${API_BASE_URL}/invites/accept`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						token,
						first_name: firstName.trim(),
						last_name: lastName.trim(),
						password,
					}),
				});

				const body = await res.json().catch(() => null);

				if (res.ok) {
					trackUserSignedUp("email");
					// Redirect to phone verification (Step 3 of onboarding).
					// The backend never mints tokens at /invites/accept — see
					// [[I-006]] — so the only path forward is the OTP step.
					navigate({
						to: "/auth/phone-verification",
						search: { token },
					});
					return;
				}

				const detail = body?.detail;
				if (typeof detail === "string") {
					setError(detail);
				} else if (detail?.message) {
					setError(detail.message);
				} else {
					setError("Something went wrong. Please try again.");
				}
			} catch {
				setError("Unable to connect. Please check your connection and try again.");
			} finally {
				setSubmitting(false);
			}
		},
		[canSubmit, token, firstName, lastName, password, navigate],
	);

	return (
		<div className="min-h-screen flex">
			{/* Left panel */}
			<div className="hidden lg:flex lg:w-[45%] bg-primary items-center justify-center p-12">
				<div className="w-full max-w-md space-y-8 text-primary-foreground">
					<div>
						<h2 className="text-3xl font-bold">CamPainless</h2>
						<p className="text-primary-foreground/80 mt-2">
							Your AI Campaign Consultant
						</p>
					</div>
					<div className="space-y-6">
						<h3 className="text-xl font-semibold">Join the campaign</h3>
						<p className="text-primary-foreground/80 text-[15px]">
							Create your account to access the campaign dashboard and start
							collaborating with your team.
						</p>
					</div>
					<div className="space-y-3 text-sm text-primary-foreground/60">
						<div className="flex items-center gap-2">
							<Lock className="size-4" />
							<span>Encrypted with bcrypt</span>
						</div>
						<div className="flex items-center gap-2">
							<ShieldCheck className="size-4" />
							<span>Industry-standard security</span>
						</div>
					</div>
				</div>
			</div>

			{/* Right panel */}
			<div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-12">
				<div className="w-full max-w-md space-y-8">
					<div className="space-y-2">
						<div className="flex items-center gap-2 text-primary">
							<KeyRound className="size-6" />
							<span className="text-sm font-medium uppercase tracking-wider">
								Create Account
							</span>
						</div>
						<h1 className="text-[32px] font-semibold tracking-tight text-foreground">
							Set Up Your Account
						</h1>
						<p className="text-[15px] text-muted-foreground">
							Enter your name and choose a password to join the campaign.
						</p>
					</div>

					<form onSubmit={handleSubmit} className="space-y-5">
						{/* Name row */}
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-2">
								<Label htmlFor="first-name">First Name</Label>
								<Input
									id="first-name"
									type="text"
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									placeholder="First"
									autoComplete="given-name"
									autoFocus
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="last-name">Last Name</Label>
								<Input
									id="last-name"
									type="text"
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									placeholder="Last"
									autoComplete="family-name"
								/>
							</div>
						</div>

						{/* Password */}
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<div className="relative">
								<Input
									id="password"
									type={showPassword ? "text" : "password"}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="Enter your password"
									className="pr-10"
									autoComplete="new-password"
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? (
										<EyeOff className="size-4" />
									) : (
										<Eye className="size-4" />
									)}
								</button>
							</div>
						</div>

						{/* Strength + requirements */}
						{password.length > 0 && (
							<div className="space-y-2">
								<div className="flex items-center justify-between text-xs">
									<span className="text-muted-foreground">
										Password strength
									</span>
									<span
										className={`font-medium ${
											strength.score <= 20
												? "text-red-500"
												: strength.score <= 50
													? "text-amber-500"
													: strength.score <= 75
														? "text-blue-500"
														: "text-green-500"
										}`}
									>
										{strength.label}
									</span>
								</div>
								<Progress
									value={strength.score}
									className={`h-1.5 ${strength.color.replace("bg-", "[&>[data-slot=progress-indicator]]:bg-")}`}
								/>
								<div className="space-y-1.5 pt-1">
									{requirementResults.map((req) => (
										<div
											key={req.label}
											className="flex items-center gap-2 text-sm"
										>
											{req.met ? (
												<Check className="size-4 text-green-500 shrink-0" />
											) : (
												<X className="size-4 text-muted-foreground/50 shrink-0" />
											)}
											<span
												className={
													req.met
														? "text-green-600 dark:text-green-400"
														: "text-muted-foreground"
												}
											>
												{req.label}
											</span>
										</div>
									))}
								</div>
							</div>
						)}

						{/* Confirm password */}
						<div className="space-y-2">
							<Label htmlFor="confirm-password">Confirm Password</Label>
							<div className="relative">
								<Input
									id="confirm-password"
									type={showConfirm ? "text" : "password"}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="Confirm your password"
									className="pr-10"
									autoComplete="new-password"
								/>
								<button
									type="button"
									onClick={() => setShowConfirm(!showConfirm)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
									aria-label={
										showConfirm ? "Hide confirm password" : "Show confirm password"
									}
								>
									{showConfirm ? (
										<EyeOff className="size-4" />
									) : (
										<Eye className="size-4" />
									)}
								</button>
							</div>
							{confirmPassword.length > 0 && !passwordsMatch && (
								<p className="text-sm text-destructive">
									Passwords do not match
								</p>
							)}
							{passwordsMatch && (
								<p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
									<Check className="size-3.5" />
									Passwords match
								</p>
							)}
						</div>

						{/* Error */}
						{error && (
							<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
								<p className="text-sm text-destructive">{error}</p>
							</div>
						)}

						<Button
							type="submit"
							size="lg"
							className="w-full text-[15px] font-medium"
							disabled={!canSubmit}
						>
							{submitting ? "Creating Account..." : "Create Account & Join"}
						</Button>
					</form>

					<div className="text-center">
						<button
							type="button"
							onClick={() =>
								navigate({ to: "/auth/accept-invite", search: { token } })
							}
							className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
						>
							<ArrowLeft className="size-3.5" />
							Back to authentication options
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
